import { useRef, useState } from "react";
import { Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export const BAG_TYPES = [
  ["checked_large", "Large checked suitcase"], ["checked_medium", "Medium checked suitcase"],
  ["checked_small", "Small checked suitcase"], ["carry_on_suitcase", "Carry-on suitcase"],
  ["carry_on_bag", "Carry-on bag"], ["personal_item", "Personal-item bag"],
  ["duffel", "Duffel bag"], ["travel_backpack", "Travel backpack"], ["daypack", "Daypack"],
  ["laptop_bag", "Laptop bag"], ["briefcase", "Briefcase"], ["garment_bag", "Garment bag"],
  ["tote", "Tote bag"], ["camera_bag", "Camera bag"], ["sports_bag", "Sports bag"],
  ["kids_luggage", "Children's luggage"], ["diaper_bag", "Diaper bag"], ["baby_bag", "Baby bag"],
  ["medical_bag", "Medical / equipment bag"], ["mobility_equipment", "Mobility equipment"],
  ["stroller", "Stroller"], ["car_seat", "Car seat / booster"], ["other", "Other specialty bag"],
];

export const ITEM_CATEGORIES = [
  ["clothing", "Clothing"], ["shoes", "Shoes"], ["toiletries", "Toiletries & personal care"],
  ["electronics", "Electronics"], ["documents", "Documents"], ["medication", "Medication"],
  ["kids", "Children's items"], ["baby", "Baby / infant"], ["comfort", "Comfort & accessories"],
  ["accessories", "Small bags & organizers"], ["food_drink", "Food & drink"],
  ["mobility", "Mobility / assistive"], ["valuables", "Valuables & jewelry"], ["other", "Other"],
];

export const BAG_STATUSES = [
  ["at_home", "At home"], ["packed", "Packed"], ["ready_to_travel", "Ready to travel"],
  ["in_vehicle", "In vehicle"], ["at_airport", "At airport"], ["checked_in", "Checked in"],
  ["carry_on", "Carry-on"], ["gate_checked", "Gate checked"], ["on_plane", "On airplane"],
  ["in_transit", "In transit"], ["at_destination", "At destination"], ["at_hotel", "At hotel / rental"],
  ["repacked", "Repacked"], ["returned_home", "Returned home"], ["lost", "Lost"],
  ["delayed", "Delayed"], ["damaged", "Damaged"], ["found", "Found"], ["left_behind", "Left behind"],
];

export const ITEM_STATUSES = [
  ["not_packed", "Not packed"], ["packed", "Packed"], ["in_use", "In use"], ["laundry", "Laundry"],
  ["damaged", "Damaged"], ["missing", "Missing"], ["purchased_on_trip", "Purchased on trip"],
  ["left_behind", "Left behind"], ["returned_home", "Returned home"],
];

export const label = (list, key) => (list.find(([k]) => k === key) || [null, key])[1];

export const statusTone = (s) =>
  ["lost", "missing", "left_behind", "damaged"].includes(s) ? "bg-red-100 text-red-700"
    : ["packed", "returned_home", "found", "checked_in", "carry_on"].includes(s) ? "bg-emerald-100 text-emerald-700"
    : ["at_home", "not_packed"].includes(s) ? "bg-zinc-100 text-zinc-600"
    : "bg-blue-100 text-blue-700";

export const inp = "w-full border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary";
export const lbl = "block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1";

/* Photo upload: encourage travelers to photograph every bag & valuable item —
   it makes identification at the baggage belt (and lost reports) far easier. */
export const PhotoUpload = ({ value, onChange, testid = "gear-photo-upload" }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/gear/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Photo saved — great for identifying it at the baggage belt!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };
  const src = value?.startsWith("/api/") ? process.env.REACT_APP_BACKEND_URL + value : value;
  return (
    <div className="flex items-center gap-3">
      {value
        ? <img src={src} alt="" className="w-16 h-16 object-cover border border-zinc-300" />
        : <div className="w-16 h-16 border border-dashed border-zinc-300 flex items-center justify-center text-zinc-400"><Camera className="w-6 h-6" /></div>}
      <div>
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={testid}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border border-zinc-300 hover:border-primary hover:text-primary transition-colors">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
          {value ? "Replace photo" : "Add photo"}
        </button>
        {value && <button type="button" onClick={() => onChange(null)} className="ml-2 text-xs text-red-600 hover:underline">Remove</button>}
        <div className="text-[11px] text-muted-foreground mt-1">A photo helps you spot it at the belt — and prove it if lost.</div>
      </div>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} />
    </div>
  );
};

export const photoSrc = (url) => (url?.startsWith("/api/") ? process.env.REACT_APP_BACKEND_URL + url : url);

export const Modal = ({ title, onClose, children, wide }) => (
  <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4"
    onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
    <div className={`bg-white border border-zinc-300 shadow-2xl w-full ${wide ? "max-w-3xl" : "max-w-xl"} my-8`}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200 bg-zinc-50">
        <h3 className="font-heading font-900 text-lg">{title}</h3>
        <button onClick={onClose} data-testid="gear-modal-close" className="text-zinc-400 hover:text-zinc-800 text-xl leading-none">×</button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);
